import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowRightLeft, Search, Eye, Truck, Package, Clock, CheckCircle2, 
  XCircle, AlertTriangle, Printer, RefreshCw, ArrowRight, FileText,
  RotateCcw, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { stockTransfersAPI, stockRequestsAPI } from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';

const statusConfig = {
  new: { label: 'New', color: 'bg-purple-100 text-purple-700', icon: Clock },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved - Pending Dispatch', color: 'bg-teal-100 text-teal-700', icon: Package },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700', icon: Package },
  ready: { label: 'Ready to Dispatch', color: 'bg-blue-100 text-blue-700', icon: Package },
  in_transit: { label: 'In Transit', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
  received: { label: 'Received', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  discrepancy: { label: 'Discrepancy', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  resolved: { label: 'Resolved', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  returned: { label: 'Returned', color: 'bg-rose-100 text-rose-700', icon: RotateCcw }
};

// Map URL paths to tab values
const tabPathMap = {
  '/warehouse/transfers': 'pending',
  '/warehouse/transfers/pending': 'pending',
  '/warehouse/transfers/dispatch': 'dispatch',
  '/warehouse/transfers/transit': 'transit',
  '/warehouse/transfers/completed': 'completed',
  '/warehouse/transfers/returns': 'returns',
  '/warehouse/transfers/outgoing': 'pending',
  '/warehouse/transfers/history': 'completed'
};

const TransfersConsolidatedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const warehouseId = user?.entity_id;
  
  // Determine initial tab from URL
  const initialTab = tabPathMap[location.pathname] || 'pending';
  
  const [transfers, setTransfers] = useState([]);
  const [pendingDispatchRequests, setPendingDispatchRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  
  // Print dialog
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);

  const fetchTransfers = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      const params = {
        location_type: 'warehouse',
        location_id: warehouseId,
        limit: 500
      };
      
      // Fetch both transfers and pending dispatch stock requests
      const [transfersRes, pendingDispatchRes] = await Promise.all([
        stockTransfersAPI.list(params),
        stockRequestsAPI.listPendingDispatch().catch(() => [])
      ]);
      
      setTransfers(transfersRes.data || []);
      setPendingDispatchRequests(pendingDispatchRes || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch transfers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, toast]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  // Update tab when URL changes
  useEffect(() => {
    const newTab = tabPathMap[location.pathname] || 'pending';
    setActiveTab(newTab);
  }, [location.pathname]);

  // Filter transfers based on active tab
  const getFilteredTransfers = () => {
    const isOutgoing = (t) => t.from_location_id === warehouseId;
    const isIncoming = (t) => t.to_location_id === warehouseId;
    
    let filtered = transfers.filter(t => {
      const matchesSearch = !search || 
        t.transfer_number?.toLowerCase().includes(search.toLowerCase()) ||
        t.to_location_name?.toLowerCase().includes(search.toLowerCase()) ||
        t.from_location_name?.toLowerCase().includes(search.toLowerCase());
      
      return matchesSearch;
    });

    switch (activeTab) {
      case 'pending':
        // Pending requests from HQ or awaiting HQ approval
        // ALSO include approved stock requests pending dispatch
        const pendingTransfers = filtered.filter(t => 
          (isOutgoing(t) && ['new', 'pending', 'pending_approval'].includes(t.status)) ||
          (isIncoming(t) && ['new', 'pending'].includes(t.status))
        );
        
        // Add pending dispatch stock requests (approved by HQ, assigned to this warehouse)
        const pendingDispatchFiltered = pendingDispatchRequests.filter(r => {
          const matchesSearch = !search || 
            r.request_number?.toLowerCase().includes(search.toLowerCase()) ||
            r.warehouse_name?.toLowerCase().includes(search.toLowerCase());
          return matchesSearch;
        }).map(r => ({
          ...r,
          // Map stock request fields to transfer-like fields for display
          transfer_number: r.request_number,
          to_location_name: r.warehouse_name,
          to_location_id: r.warehouse_id,
          from_location_id: r.source_warehouse_id,
          from_location_name: r.source_warehouse_name || 'This Warehouse',
          status: 'approved',
          is_stock_request: true,  // Flag to identify this is a stock request
          created_at: r.approved_at || r.created_at,
          total_items: r.items?.length || 0,
          total_qty: r.items?.reduce((sum, i) => sum + (i.approved_qty || 0), 0) || 0
        }));
        
        return [...pendingDispatchFiltered, ...pendingTransfers];
        
      case 'dispatch':
        // Ready to dispatch - outgoing from this warehouse in any pre-dispatch
        // state. We include 'new' (direct-create defaults to NEW) so a direct
        // transfer isn't stuck in Pending with no way to actually dispatch.
        return filtered.filter(t => 
          isOutgoing(t) && ['new', 'processing', 'pending', 'draft'].includes(t.status)
        );
      case 'transit':
        // In transit
        return filtered.filter(t => 
          t.status === 'in_transit' && (isOutgoing(t) || isIncoming(t))
        );
      case 'completed':
        // Completed/Received
        return filtered.filter(t => 
          ['received', 'completed', 'resolved'].includes(t.status)
        );
      case 'returns':
        // Returns or discrepancies
        return filtered.filter(t => 
          ['returned', 'discrepancy'].includes(t.status) ||
          t.is_return === true
        );
      default:
        return filtered;
    }
  };

  const filteredTransfers = getFilteredTransfers();

  // Count transfers for each tab
  const counts = {
    pending: transfers.filter(t => {
      const isOutgoing = t.from_location_id === warehouseId;
      const isIncoming = t.to_location_id === warehouseId;
      return (isOutgoing && ['new', 'pending', 'pending_approval'].includes(t.status)) ||
             (isIncoming && ['new', 'pending'].includes(t.status));
    }).length + pendingDispatchRequests.length,  // Include pending dispatch stock requests
    dispatch: transfers.filter(t => 
      t.from_location_id === warehouseId && ['new', 'processing', 'pending', 'draft'].includes(t.status)
    ).length,
    transit: transfers.filter(t => 
      t.status === 'in_transit' && (t.from_location_id === warehouseId || t.to_location_id === warehouseId)
    ).length,
    completed: transfers.filter(t => 
      ['received', 'completed', 'resolved'].includes(t.status)
    ).length,
    returns: transfers.filter(t => 
      ['returned', 'discrepancy'].includes(t.status) || t.is_return === true
    ).length
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    // Update URL to match tab
    const paths = {
      pending: '/warehouse/transfers/pending',
      dispatch: '/warehouse/transfers/dispatch',
      transit: '/warehouse/transfers/transit',
      completed: '/warehouse/transfers/completed',
      returns: '/warehouse/transfers/returns'
    };
    navigate(paths[value] || '/warehouse/transfers');
  };

  const handleViewDetails = (transfer) => {
    setSelectedTransfer(transfer);
    setActionNotes('');
    setDetailDialogOpen(true);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedTransfer) return;
    
    setActionLoading(true);
    try {
      await stockTransfersAPI.updateStatus(selectedTransfer.id, {
        new_status: newStatus,
        notes: actionNotes
      });
      
      toast({ title: 'Success', description: `Transfer status updated to ${statusConfig[newStatus]?.label}` });
      setDetailDialogOpen(false);
      fetchTransfers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to update status', 
        variant: 'destructive' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewHistory = async (transfer) => {
    try {
      const res = await stockTransfersAPI.getHistory(transfer.id);
      setTransferHistory(res.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch history', variant: 'destructive' });
    }
  };

  const handlePrintDispatch = async (transfer) => {
    try {
      const res = await stockTransfersAPI.getPrintDispatch(transfer.id);
      setPrintData(res.data);
      setPrintDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to get print data', variant: 'destructive' });
    }
  };

  const printDispatchList = () => {
    const printContent = document.getElementById('dispatch-print-content');
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Dispatch List - ${printData?.transfer_number}</title>
          <style>
            body { font-family: monospace; font-size: 12px; margin: 10px; }
            .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .section { margin: 10px 0; }
            .row { display: flex; justify-content: space-between; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 5px; text-align: left; }
            th { background: #f0f0f0; }
            .total { font-weight: bold; margin-top: 10px; text-align: right; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const getNextActions = (transfer) => {
    const isSource = transfer.from_location_id === warehouseId;
    const isDestination = transfer.to_location_id === warehouseId;
    
    switch (transfer.status) {
      case 'new':
        if (isSource) return [{ label: 'Dispatch', status: 'in_transit', color: 'bg-indigo-500' }];
        break;
      case 'pending':
        if (isSource) return [{ label: 'Dispatch', status: 'in_transit', color: 'bg-indigo-500' }];
        break;
      case 'processing':
        // Backend goes directly from processing to in_transit (no 'ready' status)
        if (isSource) return [{ label: 'Dispatch', status: 'in_transit', color: 'bg-indigo-500' }];
        break;
      case 'in_transit':
        if (isDestination) return [{ label: 'Mark Received', status: 'received', color: 'bg-emerald-500' }];
        break;
      case 'discrepancy':
        if (isSource) return [{ label: 'Resolve', status: 'resolved', color: 'bg-cyan-500' }];
        break;
      default:
        return [];
    }
    return [];
  };

  const renderTransferRow = (transfer) => {
    const StatusIcon = statusConfig[transfer.status]?.icon || Clock;
    const isSource = transfer.from_location_id === warehouseId;
    const actions = getNextActions(transfer);
    
    return (
      <TableRow key={transfer.id} className="hover:bg-gray-50" data-testid={`transfer-row-${transfer.id}`}>
        <TableCell>
          <div className="font-medium text-gray-800">{transfer.transfer_number}</div>
          <div className="text-xs text-gray-400">{formatDateOnly(transfer.created_at)}</div>
        </TableCell>
        <TableCell>
          <div className="text-sm">{transfer.from_location_name}</div>
          <div className="text-xs text-gray-400">{transfer.from_location_type}</div>
        </TableCell>
        <TableCell>
          <div className="text-sm">{transfer.to_location_name}</div>
          <div className="text-xs text-gray-400">{transfer.to_location_type}</div>
        </TableCell>
        <TableCell className="text-right font-medium">{transfer.total_items}</TableCell>
        <TableCell className="text-right">{transfer.total_quantity}</TableCell>
        <TableCell className="text-center">
          <Badge className={statusConfig[transfer.status]?.color}>
            {statusConfig[transfer.status]?.label || transfer.status}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-1">
            {actions.length > 0 && (
              <Button 
                size="sm" 
                className={`${actions[0].color} hover:opacity-90 text-white`}
                onClick={() => handleViewDetails(transfer)}
                data-testid={`action-btn-${transfer.id}`}
              >
                {actions[0].label}
              </Button>
            )}
            {transfer.status === 'processing' && isSource && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handlePrintDispatch(transfer)}
                data-testid={`print-btn-${transfer.id}`}
              >
                <Printer className="w-4 h-4" />
              </Button>
            )}
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => handleViewDetails(transfer)}
              data-testid={`view-btn-${transfer.id}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => handleViewHistory(transfer)}
              data-testid={`history-btn-${transfer.id}`}
            >
              <FileText className="w-4 h-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="warehouse-transfers-consolidated">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stock Transfers</h1>
          <p className="text-gray-500">Manage all incoming and outgoing stock transfers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchTransfers} className="border-gray-300" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => navigate('/warehouse/transfers/new')}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="new-transfer-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            New Transfer
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className={`cursor-pointer transition-all ${activeTab === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => handleTabChange('pending')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Pending</p>
                <p className="text-xl font-bold text-amber-600">{counts.pending}</p>
              </div>
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${activeTab === 'dispatch' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => handleTabChange('dispatch')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Ready</p>
                <p className="text-xl font-bold text-blue-600">{counts.dispatch}</p>
              </div>
              <Package className="w-6 h-6 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${activeTab === 'transit' ? 'ring-2 ring-indigo-500' : ''}`}
          onClick={() => handleTabChange('transit')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">In Transit</p>
                <p className="text-xl font-bold text-indigo-600">{counts.transit}</p>
              </div>
              <Truck className="w-6 h-6 text-indigo-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${activeTab === 'completed' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => handleTabChange('completed')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-xl font-bold text-green-600">{counts.completed}</p>
              </div>
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${activeTab === 'returns' ? 'ring-2 ring-rose-500' : ''}`}
          onClick={() => handleTabChange('returns')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Returns</p>
                <p className="text-xl font-bold text-rose-600">{counts.returns}</p>
              </div>
              <RotateCcw className="w-6 h-6 text-rose-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs & Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full md:w-auto">
              <TabsList className="bg-gray-100 grid grid-cols-5 w-full md:w-auto">
                <TabsTrigger value="pending" className="text-xs" data-testid="tab-pending">
                  Pending {counts.pending > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px] px-1">{counts.pending}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="dispatch" className="text-xs" data-testid="tab-dispatch">
                  Ready {counts.dispatch > 0 && <Badge className="ml-1 bg-blue-500 text-white text-[10px] px-1">{counts.dispatch}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="transit" className="text-xs" data-testid="tab-transit">
                  Transit {counts.transit > 0 && <Badge className="ml-1 bg-indigo-500 text-white text-[10px] px-1">{counts.transit}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="completed" className="text-xs" data-testid="tab-completed">
                  Done
                </TabsTrigger>
                <TabsTrigger value="returns" className="text-xs" data-testid="tab-returns">
                  Returns
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search transfers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-white border-gray-300"
                data-testid="search-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
            </div>
          ) : filteredTransfers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ArrowRightLeft className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No transfers found</p>
              <p className="text-sm mt-1">Transfers matching the selected tab will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Transfer #</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransfers.map(transfer => renderTransferRow(transfer))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transfer Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-teal-500" />
              Transfer Details - {selectedTransfer?.transfer_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransfer && (
            <div className="space-y-4">
              {/* Status and Route */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-500">From</div>
                  <div className="font-medium">{selectedTransfer.from_location_name}</div>
                  <div className="text-xs text-gray-400">{selectedTransfer.from_location_type}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
                <div className="text-right">
                  <div className="text-sm text-gray-500">To</div>
                  <div className="font-medium">{selectedTransfer.to_location_name}</div>
                  <div className="text-xs text-gray-400">{selectedTransfer.to_location_type}</div>
                </div>
                <Badge className={statusConfig[selectedTransfer.status]?.color}>
                  {statusConfig[selectedTransfer.status]?.label}
                </Badge>
              </div>
              
              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTransfer.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="font-medium">{item.product_name}</div>
                          <div className="text-xs text-gray-400">{item.sku}</div>
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">₹{(item.line_value || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Summary */}
              <div className="bg-teal-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Total Items</p>
                    <p className="font-bold text-teal-700">{selectedTransfer.total_items}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Quantity</p>
                    <p className="font-bold text-teal-700">{selectedTransfer.total_quantity}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Value</p>
                    <p className="font-bold text-teal-700">₹{(selectedTransfer.total_value || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              {/* Action Notes */}
              {getNextActions(selectedTransfer).length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
                  <Textarea
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    placeholder="Add notes for this action..."
                    className="bg-white border-gray-300"
                    data-testid="action-notes"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
            {selectedTransfer && getNextActions(selectedTransfer).map((action, idx) => (
              <Button 
                key={idx}
                className={`${action.color} hover:opacity-90 text-white`}
                onClick={() => handleStatusUpdate(action.status)}
                disabled={actionLoading}
                data-testid="confirm-action-btn"
              >
                {actionLoading ? 'Processing...' : action.label}
              </Button>
            ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Transfer History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {transferHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No history available</p>
              </div>
            ) : (
              transferHistory.map((entry, idx) => (
                <div key={entry.id || idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={statusConfig[entry.to_status]?.color || 'bg-gray-100 text-gray-700'}>
                      {statusConfig[entry.to_status]?.label || entry.to_status}
                    </Badge>
                    <span className="text-xs text-gray-400">{formatDate(entry.timestamp)}</span>
                  </div>
                  <div className="text-sm text-gray-600">{entry.user_name}</div>
                  {entry.notes && <div className="text-xs text-gray-500 mt-1">{entry.notes}</div>}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Dispatch Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Print Dispatch List</DialogTitle>
          </DialogHeader>
          <div id="dispatch-print-content" className="font-mono text-sm">
            <div className="header">
              <h2 className="text-lg font-bold">DISPATCH LIST</h2>
              <p>{printData?.transfer_number}</p>
            </div>
            <div className="section">
              <div className="row"><span>From:</span><span>{printData?.from_location?.name}</span></div>
              <div className="row"><span>To:</span><span>{printData?.to_location?.name}</span></div>
              <div className="row"><span>Date:</span><span>{formatDate(printData?.transfer_date)}</span></div>
            </div>
            <table>
              <thead>
                <tr><th>Product</th><th>SKU</th><th>Qty</th></tr>
              </thead>
              <tbody>
                {printData?.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.product_name}</td>
                    <td>{item.sku}</td>
                    <td>{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="total">Total Items: {printData?.total_items} | Total Qty: {printData?.total_qty}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>Close</Button>
            <Button onClick={printDispatchList} className="bg-teal-500 hover:bg-teal-600">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransfersConsolidatedPage;
